import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TimesManager } from '@/components/admin/TimesManager';
import UsersTable from '@/components/admin/user-management/UsersTable';
import UserManager from '@/components/admin/user-management/UserManager';
import { useUserDashboardData } from '@/hooks/useUserDashboardData';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { AlertCircle } from 'lucide-react';

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const { language, direction } = useLanguage();
  const { user: userData } = useUserDashboardData();
  const { isSuper } = useRolePermissions();
  
  const isRtl = direction === 'rtl';
  
  if (!isSuper) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          {direction === 'rtl' ? 'אין לך הרשאות מתאימות' : 'Unauthorized'}
        </h2>
        <p className="text-gray-600">
          {direction === 'rtl'
            ? 'אינך מורשה לגשת לאזור זה.'
            : 'You do not have permission to access this area.'}
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <h1 className={`text-2xl font-bold mb-8 ${isRtl ? 'text-right' : 'text-left'}`}>
        {isRtl ? 'פאנל ניהול' : 'Admin Panel'}
      </h1>
      
      <Tabs defaultValue="users" className="w-full">
        <TabsList>
          <TabsTrigger value="users">{isRtl ? 'משתמשים' : 'Users'}</TabsTrigger>
          <TabsTrigger value="times">{isRtl ? 'זמנים' : 'Times'}</TabsTrigger>
          {/* Add more tabs here as needed */}
        </TabsList>
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>{isRtl ? 'ניהול משתמשים' : 'User Management'}</CardTitle>
              <CardDescription>
                {isRtl
                  ? 'נהל משתמשים קיימים, הוסף משתמשים חדשים וערוך תפקידים.'
                  : 'Manage existing users, add new users, and edit roles.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <UserManager />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="times">
          <Card>
            <CardHeader>
              <CardTitle>{isRtl ? 'ניהול זמנים' : 'Times Management'}</CardTitle>
              <CardDescription>
                {isRtl
                  ? 'נהל זמנים לתפילות, חגים ואירועים מיוחדים.'
                  : 'Manage times for prayers, holidays, and special events.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TimesManager />
            </CardContent>
          </Card>
        </TabsContent>
        {/* Add more tab contents here as needed */}
      </Tabs>
    </div>
  );
};

export default AdminPanel;
